// @ts-nocheck
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {BaseSpinner} from "@/components";
import logo from "../../../assets/images/coronando-logo.svg";
import {getPaykuTransactionStatus} from "../../../services/wallet/payku.service";
import {useAuthStore} from "../../../store/useAuth.store";

const paykuContainerCls =
  "min-h-screen flex flex-col items-center justify-center p-5 bg-[var(--background-light,var(--color-gray-5))]";
const logoContainerCls = "mb-[30px]";
const logoCls = "max-w-[250px] h-auto max-[600px]:max-w-[var(--size-45)]";
const cardCls =
  "bg-[var(--background,var(--color-surface-3))] rounded-[var(--radius-xl)] border border-[var(--border,rgba(255,255,255,0.1))] p-10 max-w-[var(--size-125)] w-full text-center shadow-[0_8px_32px_rgba(0,0,0,0.3)] max-[600px]:p-6 max-[600px]:m-2.5";
const iconContainerCls = "mb-6";
const successIconCls =
  "text-[var(--font-display)] text-[var(--color-success)] motion-safe:animate-payku-pulse max-[600px]:text-[var(--font-display)]";
const failedIconCls =
  "text-[var(--font-display)] text-[var(--color-danger)] motion-safe:animate-payku-shake max-[600px]:text-[var(--font-display)]";
const pendingIconCls =
  "text-[var(--font-display)] text-[var(--hover,var(--color-primary-hover))] motion-safe:animate-payku-spin max-[600px]:text-[var(--font-display)]";
const titleCls =
  "text-[var(--font-h1)] font-bold mb-4 text-[var(--text-secondary,var(--color-white))] max-[600px]:text-[var(--font-h2)]";
const successTextCls = "!text-[var(--color-success)]";
const failedTextCls = "!text-[var(--color-danger)]";
const pendingTextCls = "!text-[var(--hover,var(--color-primary-hover))]";
const messageCls =
  "text-base text-[var(--text-secondary,rgba(255,255,255,0.75))] mb-6 leading-[1.6] max-[600px]:text-sm";
const detailsContainerCls = "bg-[var(--background-light,rgba(255,255,255,0.05))] rounded-xl p-4 mb-6";
const detailCls =
  "flex justify-between items-center py-2 m-0 border-b border-[var(--border,rgba(255,255,255,0.1))] last:border-b-0";
const detailLabelCls = "text-[var(--gray,rgba(255,255,255,0.6))] text-sm";
const detailValueCls = "text-[var(--text-secondary,var(--color-white))] font-semibold text-sm";
const redirectContainerCls = "mt-5";
const redirectTextCls = "text-[var(--gray,rgba(255,255,255,0.6))] text-sm mb-3";
const progressBarCls =
  "h-1 bg-[var(--background-light-secondary,rgba(255,255,255,0.1))] rounded-sm overflow-hidden mb-5";
const progressFillCls =
  "h-full bg-[var(--primary,var(--color-primary))] rounded-sm transition-[width] duration-1000 ease-linear";
const redirectButtonCls =
  "bg-[var(--button-background,var(--color-primary))] border-0 text-[var(--text-primary,var(--color-black))] py-3 px-8 text-base font-semibold rounded-lg cursor-pointer transition-all duration-300 font-[var(--font-family,'Poppins',sans-serif)] hover:bg-[var(--button-hover,var(--color-primary-hover))] hover:-translate-y-0.5 active:translate-y-0 active:bg-[var(--button-pressed,var(--color-primary-accent))]";

const PaykuCallback = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auth state
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const [status, setStatus] = useState("loading"); // loading, success, failed, pending
  const [countdown, setCountdown] = useState(10);
  const [transactionDetails, setTransactionDetails] = useState(null);

  // Get order ID from URL params
  const orderId = searchParams.get("order") || searchParams.get("orderId");

  // Check authentication - only redirect when we're CERTAIN user is not authenticated
  // authStatus can be: 'checking' (initial), 'authenticated', or 'unauthenticated'
  // We wait for 'unauthenticated' to avoid redirecting during initial session validation
  useEffect(() => {
    if (authStatus === "unauthenticated") {
      // Redirect to home and trigger login modal
      navigate("/");
      // Small delay to ensure navigation completes before showing modal
      setTimeout(() => {
        handleShowAuthModal();
      }, 100);
    }
  }, [authStatus, navigate, handleShowAuthModal]);

  // Fetch transaction status on mount (only if authenticated)
  useEffect(() => {
    if (authStatus !== "authenticated") return; // Wait for auth check to complete

    const fetchStatus = async () => {
      // If no orderId, show pending (user may have navigated here before completing payment)
      if (!orderId) {
        setStatus("pending");
        return;
      }

      try {
        const response = await getPaykuTransactionStatus(orderId);
        const transactionData = response?.data?.data;
        const actualStatus = transactionData?.status;

        setTransactionDetails({
          orderId: orderId,
          amount: transactionData?.amount,
          currency: transactionData?.currency || "CLP",
        });

        if (actualStatus === "approved" || actualStatus === "completed") {
          setStatus("success");
        } else if (actualStatus === "rejected" || actualStatus === "expired" || actualStatus === "cancelled") {
          setStatus("failed");
        } else {
          // For pending or unknown status, treat as pending/processing
          setStatus("pending");
        }
      } catch (_error) {
        // On API error, show pending instead of failed (transaction may still be processing)
        setStatus("pending");
      }
    };

    fetchStatus();
  }, [orderId, authStatus]);

  // Countdown timer and redirect (only for success or failed status, NOT for pending)
  useEffect(() => {
    // Don't start countdown if loading, pending, or not authenticated
    if (status === "loading" || status === "pending" || authStatus !== "authenticated") return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, navigate, authStatus]);

  // Show loading while auth is being checked, or before redirect completes
  if (authStatus === "checking" || authStatus === "unauthenticated") {
    return (
      <div className={paykuContainerCls}>
        <div className={logoContainerCls}>
          <img
            src={logo}
            alt="RYG Logo"
            className={logoCls}
          />
        </div>
        <div className={cardCls}>
          <BaseSpinner size="xl" />
          <p className={messageCls}>{t("paykuCallbackVerifyingMessage")}</p>
        </div>
      </div>
    );
  }

  const renderStatusIcon = () => {
    if (status === "loading") {
      return <BaseSpinner size="xl" />;
    }
    if (status === "success") {
      return (
        <span
          className={successIconCls}
          aria-hidden="true"
        >
          ✓
        </span>
      );
    }
    if (status === "pending") {
      return (
        <BaseSpinner
          size="xl"
          className={pendingIconCls}
        />
      );
    }
    return (
      <span
        className={failedIconCls}
        aria-hidden="true"
      >
        ✕
      </span>
    );
  };

  const getStatusTitle = () => {
    if (status === "loading") {
      return t("paykuCallbackVerifying");
    }
    if (status === "success") {
      return t("paykuCallbackSuccess");
    }
    if (status === "pending") {
      return t("paykuCallbackPending");
    }
    return t("paykuCallbackFailed");
  };

  const getStatusMessage = () => {
    if (status === "loading") {
      return t("paykuCallbackVerifyingMessage");
    }
    if (status === "success") {
      return t("paykuCallbackSuccessMessage");
    }
    if (status === "pending") {
      return t("paykuCallbackPendingMessage");
    }
    return t("paykuCallbackFailedMessage");
  };

  return (
    <div className={paykuContainerCls}>
      <div className={logoContainerCls}>
        <img
          src={logo}
          alt="RYG Logo"
          className={logoCls}
        />
      </div>

      <div className={cardCls}>
        <div className={iconContainerCls}>{renderStatusIcon()}</div>

        <h1
          className={`${titleCls} ${status === "success" ? successTextCls : status === "pending" ? pendingTextCls : status === "failed" ? failedTextCls : ""}`}
        >
          {getStatusTitle()}
        </h1>

        <p className={messageCls}>{getStatusMessage()}</p>

        {transactionDetails && status !== "loading" && (
          <div className={detailsContainerCls}>
            {transactionDetails.orderId && (
              <p className={detailCls}>
                <span className={detailLabelCls}>{t("paykuCallbackOrderId")}:</span>
                <span className={detailValueCls}>{transactionDetails.orderId}</span>
              </p>
            )}
            {transactionDetails.amount && (
              <p className={detailCls}>
                <span className={detailLabelCls}>{t("paykuCallbackAmount")}:</span>
                <span className={detailValueCls}>
                  {transactionDetails.amount.toLocaleString()} {transactionDetails.currency}
                </span>
              </p>
            )}
          </div>
        )}

        {(status === "success" || status === "failed") && (
          <div className={redirectContainerCls}>
            <p className={redirectTextCls}>{t("paykuCallbackRedirectMessage", {seconds: countdown})}</p>
            <div className={progressBarCls}>
              <div
                className={progressFillCls}
                style={{width: `${((10 - countdown) / 10) * 100}%`}}
              />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => navigate("/")}
          className={redirectButtonCls}
        >
          {t("paykuCallbackGoHome")}
        </button>
      </div>
    </div>
  );
};

export default PaykuCallback;
